import React, { useMemo } from "react";
import { MRT_ColumnDef } from "material-react-table";
import { Box, Link } from "@mui/material";
import CustomButton from "../../components/custom-button/CustomButton";
import CustomTable from "../../components/custom-table/CustomTable";
import { formatDate } from "../../utility/functions/common-functions";
import { useNavigate } from "react-router-dom";
import { MANAGE_ADMIN_PLACEHOLDER } from "../../utility/constants/common-constants";
import { PAGE_HEADER_ADD_ADMIN } from "../../utility/constants/locale";

// Define the User interface to keep the data structure clear
interface User {
  displayName: string;
  emailId: string;
  createdDateTime: string;
  userRoleName: string;
  status: string;
  location: string;
  userId: number;
  statusId: number;
  userRoleId: number;
}

interface ManageUsersProps {
  formatDataForTable: User[];
}

const ManageUsers: React.FC<ManageUsersProps> = ({ formatDataForTable }) => {
  const navigate = useNavigate();

  const navigateAddAdmin = () => {
    navigate("/admin/add-user");
  };

  // Define the columns for the table using `useMemo` for optimization
  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "displayName",
        header: "Name",
        filterFn: "contains",
        Cell: ({ row, renderedCellValue }) => {
          const handleClick = () => {
            navigate(`/admin/edit-user/${row.original?.userId}`, {
              state: {
                userId: row.original?.userId,
                emailId: row.original?.emailId,
                displayName: row.original.displayName,
                userRoleId: row.original?.userRoleId,
                statusId: row.original?.statusId,
              },
            });
          };

          return (
            <Link
              href="#"
              sx={{ fontWeight: 500, textDecoration: "underline" }}
              onClick={handleClick}
            >
              {renderedCellValue}
            </Link>
          );
        },
        size: 120,
      },
      {
        accessorKey: "emailId",
        header: "Email ID",
        filterFn: "contains",
        enableGlobalFilter: false,
        sortingFn: "customSortFn",
      },
      {
        accessorKey: "formattedDate",
        header: "Effective Date",
        Cell: ({ renderedCellValue }) => (
          <span>{formatDate(renderedCellValue as string)}</span>
        ),
        filterFn: "contains",
        enableGlobalFilter: false,
        size: 120,
      },
      {
        accessorKey: "userRoleDisplayName",
        header: "Role",
        size: 130,
        filterFn: "startsWith",
      },
      {
        accessorKey: "status",
        header: "User Status",
        Cell: ({ renderedCellValue }) => (
          <span
            style={{
              display: "inline-block",
              padding: "2px 20px",
              borderRadius: "100px",
              backgroundColor:
                renderedCellValue === "Active" ? "#06A3B04D" : "#D0A6CD66",
            }}
          >
            {renderedCellValue}
          </span>
        ),
        filterFn: "startsWith",
        size: 130,
      },
    ],
    [navigate]
  );

  // Define toolbar actions
  const renderTopToolbarCustomActions = () => (
    <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
      <CustomButton
        variant="contained"
        text={PAGE_HEADER_ADD_ADMIN}
        onClick={navigateAddAdmin}
      />
    </Box>
  );

  return (
    <div className="ManageUser">
      <CustomTable
        data={formatDataForTable}
        columns={columns}
        title="Admin List"
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        totalRecords={formatDataForTable?.length}
        globalFilterPlaceholder={MANAGE_ADMIN_PLACEHOLDER}
      />
    </div>
  );
};

export default ManageUsers;
